import { DATETIME_FORMAT } from '@/constants';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { GetProp, UploadProps } from 'antd';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// 使用插件
dayjs.extend(utc);
dayjs.extend(customParseFormat);

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
export const getLoginStatus = () => !!sessionStorage.getItem('token');

export const refetchQueriesParams = (key: any): any => {
  return {
    queryKey: key,
    type: 'active',
    exact: true,
  };
};

/**
 * @description input输入规则
 * @param field 请输入
 * @param res
 */
export const inputRules = (field?: string, ...res: any[]) => {
  return field
    ? [{ required: true, whitespace: true, message: `请输入${field}` }, ...res]
    : [{ required: true, whitespace: true }, ...res];
};

/**
 * @description 字节转换KB->GB
 * @param value 字段
 */
export const convertKBToGB = (value: number): number => {
  return Number((value / 1024 / 1024).toFixed(4));
};

/**
 * @description 字节转换GB->B
 * @param value 字段
 */
export const convertGBToKB = (value: number): number => {
  return Math.floor(value * 1024 * 1024);
};

/**
 * 根据时间戳和时区转换时间
 * @param time 时间戳，单位为毫秒
 * @param timeZone 时区字符串（例如 "Asia/Shanghai"）
 * @returns 格式化后的时间字符串
 */
export const convertTimestampToTimeZone = (time: string | number, timeZone: string): string => {
  if (!time || !timeZone) return ''; // 参数校验

  // 转换时间戳为 Dayjs 对象并处理时区
  const localTime = dayjs(Number(time))?.tz(timeZone);

  // 格式化为 "YYYY-MM-DD HH:mm:ss" 格式
  return localTime?.format('YYYY-MM-DD HH:mm:ss');
};

/**
 * @description local 转 UTC
 * @param param
 */
export const convertLocalTtoUTC = (param?: string | Date | Dayjs) => {
  if (dayjs(param).isValid()) {
    return dayjs.utc(param);
  }
};

/**
 * @description 判断域名
 * @param name
 */
export const dnsQuery = async (name: any) => {
  const url = `https://cloudflare-dns.com/dns-query?name=${name}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/dns-json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data?.Status;
  } catch (error) {
    return null;
  }
};

/**
 * @description 根据要求格式显示时间
 * @param param
 * @param format
 */
export const getLocalDateTime = (
  param: string | Date | Dayjs,
  format = DATETIME_FORMAT,
) => {
  if (dayjs(param).isValid()) {
    return dayjs.utc(param).local().format(format);
  }
  return '';
};

/**
 * @description 根据要求格式显示时间
 * @param param
 * @param format
 */
export const getDateTime = (
  param: string | Date | Dayjs,
  format = DATETIME_FORMAT,
) => {
  if (dayjs(param).isValid()) {
    return dayjs(param).format(format);
  }
  return '';
};

/**
 * @description 获得图标base64
 * @param file
 */
export const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

// export const sumData = (param: any, byMonth: boolean = false) => {
//   const sumByCount: any = {}; // 用来存储总次数
//   const sumByType: any = {}; // 用来存储按类型统计的次数
//
//   param?.forEach((item: any) => {
//     const key = byMonth
//       ? dayjs(item.date).format('YYYY-MM') // 按月统计
//       : item.date; // 按日统计
//
//     // 按日期或按月份统计总次数
//     sumByCount[key] = (sumByCount[key] || 0) + 1;
//
//     // 按日期或按月份和设备类型统计次数
//     if (!sumByType[key]) {
//       sumByType[key] = { android: 0, ios: 0, windows: 0}; // 初始化每个日期或月份的设备类型统计
//     }
//
//     // 根据设备类型进行统计
//     if (item.type === 'android') {
//       sumByType[key].android += 1;
//     } else if (item.type === 'ios') {
//       sumByType[key].ios += 1;
//     }else if (item.type === 'windows') {
//       sumByType[key].windows += 1;
//     }
//   });
//
//   return { sumByCount, sumByType };
// };

export const sumData = (param: any[] = [], byMonth = false) => {
  const sumByCount: Record<string, number> = {};
  const sumByType: Record<string, { android: number; ios: number; windows: number }> = {};

  for (const item of param) {
    const key = byMonth ? dayjs(item.date).format('YYYY-MM') : item.date;
    if (!key) continue;
    sumByCount[key] = (sumByCount[key] ?? 0) + 1;
    sumByType[key] ??= { android: 0, ios: 0, windows: 0 };

    const t = String(item.type || '').toLowerCase().trim();
    if (t === 'android') sumByType[key].android++;
    else if (t === 'ios') sumByType[key].ios++;
    else if (t === 'windows') sumByType[key].windows++;
  }
  return { sumByCount, sumByType };
};

/**
 * @description 安装类型统计
 * @param param
 */
export const sumByTypeCount = (param: any) => {
  const sumByType: any = { android: 0, ios: 0, windows: 0 }; // 初始化android和ios的计数
  param?.forEach((item: any) => {
    // 根据type字段累加相应的计数
    if (item.type === 'android') {
      sumByType.android += 1; // 增加android计数
    } else if (item.type === 'ios') {
      sumByType.ios += 1; // 增加ios计数
    }else if (item.type === 'windows') {
      sumByType.windows += 1;
    }
  });
  return sumByType; // 返回统计结果
};

/**
 * @description 从url获取文件名
 * @param url
 */
export const getFileNameFromURL = (url: string) => {
  // 使用正则表达式从URL中提取文件名
  const matches = url?.match?.(/[^/]+$/);
  return matches ? matches?.[0] : null;
};

export const getTimePlusSeconds = (seconds: number): string =>
  dayjs().add(seconds, 'second').format('YYYY/MM/DD HH:mm:ss');

/**
 * @description 版本比较
 * @param version1
 * @param version2
 */
export const compareVersions = (version1: any, version2: any) => {
  const v1Parts = version1?.split('.');
  const v2Parts = version2?.split('.');
  const maxLength = Math?.max(v1Parts?.length, v2Parts?.length);
  for (let i = 0; i < maxLength; i++) {
    const v1Part = parseInt(v1Parts[i] || 0);
    const v2Part = parseInt(v2Parts[i] || 0);
    if (v1Part < v2Part) {
      return -1;
    } else if (v1Part > v2Part) {
      return 1;
    }
  }
  return 0;
};
